import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Switch,
  Modal,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { burialPlotService } from '../../../../services/burialPlotInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('BurialPlotInput');

export const BurialPlotInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { burialPlotId?: string; isEdit?: boolean }>, string>>();
  const { burialPlotId, isEdit } = route.params || {};
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [hasDeed, setHasDeed] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedBurialPlotId, setSavedBurialPlotId] = useState<string | undefined>();

  const [formData, setFormData] = useState({
    cemeteryName: '',
    cemeteryAddress: '',
    cemeteryPhone: '',
    cemeteryEmail: '',
    burialPlotNumber: '',
    deedLocation: '',
    purchasePrice: '',
    purchaseDate: '',
    cemeteryWebsite: '',
    notes: '',
    username: '',
    password: '',
  });

  // Load existing burial plot data when editing
  useEffect(() => {
    logger.debug('[Edit Debug] useEffect triggered - isEdit:', isEdit, 'burialPlotId:', burialPlotId);
    if (isEdit && burialPlotId) {
      logger.debug('[Edit Debug] Loading burial plot data for editing');
      loadBurialPlotData();
    }
  }, [isEdit, burialPlotId]);

  const loadBurialPlotData = async () => {
    try {
      logger.debug('[Edit Debug] loadBurialPlotData called with ID:', burialPlotId);
      if (!burialPlotId) {
        logger.debug('[Edit Debug] No burialPlotId provided, returning');
        return;
      }

      logger.debug('[Edit Debug] Fetching data for ID:', burialPlotId);
      const data = await burialPlotService.getBurialPlotById(burialPlotId);
      logger.debug('[Edit Debug] Received data:', data);

      if (data) {
        setFormData({
          cemeteryName: data.cemeteryName || '',
          cemeteryAddress: data.cemeteryAddress || '',
          cemeteryPhone: formatPhoneNumber(data.cemeteryPhone || ''),
          cemeteryEmail: data.cemeteryEmail || '',
          burialPlotNumber: data.lotNumber || '',
          deedLocation: data.deedLocation || '',
          purchasePrice: data.purchasePrice ? formatCurrency(data.purchasePrice.toString()) : '',
          purchaseDate: formatDate(data.purchaseDate || ''),
          cemeteryWebsite: data.cemeteryWebsite || '',
          notes: data.notes || '',
          username: '',
          password: '',
        });

        // Set deed toggle based on whether deed location exists
        if (data.deedLocation) {
          setHasDeed(true);
        }

        // Load portal credentials
        try {
          const portalData = await vaultService.getPortalCredentials(burialPlotId, 'burial-plot');
          if (portalData) {
            setFormData(prev => ({
              ...prev,
              username: portalData.username || '',
              password: portalData.password || '',
            }));
            setHasOnlinePortal(true);
          }
        } catch (vaultError) {
          logger.debug('Could not load portal credentials:', vaultError);
        }
      }
    } catch (error) {
      logger.error('Error loading burial plot data:', error);
      Alert.alert('Error', 'Failed to load burial plot information');
    }
  };

  // Format currency with commas
  const formatCurrency = (value: string) => {
    // Remove non-numeric characters except decimal
    const numericValue = value.replace(/[^0-9.]/g, '');
    // Split by decimal
    const parts = numericValue.split('.');
    // Format whole number with commas
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Limit decimal places to 2
    if (parts[1]) {
      parts[1] = parts[1].substring(0, 2);
    }
    return parts.join('.');
  };

  const handleCurrencyChange = (text: string) => {
    const cleaned = text.replace(/^\$/, '');
    setFormData({ ...formData, purchasePrice: formatCurrency(cleaned) });
  };

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  const handlePhoneChange = (text: string) => {
    const formatted = formatPhoneNumber(text);
    setFormData({ ...formData, cemeteryPhone: formatted });
  };

  // Format date as dd/mm/yyyy
  const formatDate = (text: string) => {
    // Remove non-numeric characters
    const cleaned = text.replace(/\D/g, '');

    if (cleaned.length >= 8) {
      // Format as dd/mm/yyyy
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
    } else if (cleaned.length >= 4) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4)}`;
    } else if (cleaned.length >= 2) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    }
    return cleaned;
  };

  const handleDateChange = (text: string) => {
    const formatted = formatDate(text);
    setFormData({ ...formData, purchaseDate: formatted });
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.username || formData.password) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }

      // Basic validation
      if (!formData.cemeteryName) {
        Alert.alert('Validation Error', 'Cemetery name is required');
        setSubmitting(false);
        return;
      }

      // Prepare payload for API
      const payload: any = {
        cemeteryName: formData.cemeteryName,
        cemeteryAddress: formData.cemeteryAddress || undefined,
        cemeteryPhone: formData.cemeteryPhone.replace(/\D/g, '') || undefined,
        cemeteryEmail: formData.cemeteryEmail || undefined,
        lotNumber: formData.burialPlotNumber || undefined,
        deedLocation: hasDeed ? formData.deedLocation : undefined,
        purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice.replace(/[^0-9.]/g, '')) : undefined,
        purchaseDate: formData.purchaseDate || undefined,
        cemeteryWebsite: formData.cemeteryWebsite || undefined,
        notes: formData.notes || undefined,
      };

      // Create or update burial plot record
      let saved;
      let savedId;

      if (isEdit && burialPlotId) {
        saved = await burialPlotService.updateBurialPlot(burialPlotId, payload);
        savedId = burialPlotId;
      } else {
        saved = await burialPlotService.createBurialPlot(payload);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
      }

      // Store the saved ID for the Done button
      setSavedBurialPlotId(savedId);

      // Store portal credentials in Vault if provided
      if (savedId && (formData.username || formData.password)) {
        try {
          await vaultService.storePortalCredentials(savedId, 'burial-plot', {
            username: formData.username,
            password: formData.password,
          });
        } catch (e) {
          logger.error('Vault write failed for burial plot portal credentials:', e);
        }
      }

      if (isEdit) {
        // For edit mode, navigate to lock/unlock screen
        Alert.alert('Success', 'Burial plot information updated successfully!', [
          {
            text: 'OK',
            onPress: () => navigation.navigate('BurialPlotLockUnlock', { burialPlotId: burialPlotId })
          }
        ]);
      } else {
        // Clear the form and reset states for Add Another
        setFormData({
          cemeteryName: '',
          cemeteryAddress: '',
          cemeteryPhone: '',
          cemeteryEmail: '',
          burialPlotNumber: '',
          deedLocation: '',
          purchasePrice: '',
          purchaseDate: '',
          cemeteryWebsite: '',
          notes: '',
          username: '',
          password: '',
        });
        setHasOnlinePortal(false);
        setHasDeed(false);

        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
        // Reset submitting so buttons are interactive
        setSubmitting(false);
      }
    } catch (error: any) {
      logger.error('Error saving burial plot info:', error);
      Alert.alert('Error', 'Failed to save burial plot information. Please try again.');
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFormData({
      cemeteryName: '',
      cemeteryAddress: '',
      cemeteryPhone: '',
      cemeteryEmail: '',
      burialPlotNumber: '',
      deedLocation: '',
      purchasePrice: '',
      purchaseDate: '',
      cemeteryWebsite: '',
      notes: '',
      username: '',
      password: '',
    });
    setHasOnlinePortal(false);
    setHasDeed(false);
    setShowAddAnotherDone(false);
    setSubmitting(false);
  };

  const handleDone = () => {
    setSubmitting(false);
    // Navigate to lock/unlock with the saved ID
    logger.debug('[Done Debug] Using ID:', savedBurialPlotId);
    (navigation as any).navigate('BurialPlotLockUnlock', { burialPlotId: savedBurialPlotId });
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Burial Plot Info</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Cemetery Name *</Text>
            <TextInput
              style={styles.input}
              value={formData.cemeteryName}
              onChangeText={(text) => setFormData({ ...formData, cemeteryName: text })}
              placeholder="Ex: Eternal Rest Cemetery"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Cemetery Address</Text>
            <TextInput
              style={styles.input}
              value={formData.cemeteryAddress}
              onChangeText={(text) => setFormData({ ...formData, cemeteryAddress: text })}
              placeholder="123 Main St, City, State ZIP"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Cemetery Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.cemeteryPhone}
              onChangeText={handlePhoneChange}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Cemetery Email</Text>
            <TextInput
              style={styles.input}
              value={formData.cemeteryEmail}
              onChangeText={(text) => setFormData({ ...formData, cemeteryEmail: text })}
              placeholder="info@cemetery.com"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Burial Plot Number</Text>
            <TextInput
              style={styles.input}
              value={formData.burialPlotNumber}
              onChangeText={(text) => setFormData({ ...formData, burialPlotNumber: text })}
              placeholder="Ex: H345 North"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          {/* Deed Toggle */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setHasDeed(!hasDeed)}
            >
              <View style={[styles.toggle, hasDeed && styles.toggleActive]}>
                <View style={[styles.toggleThumb, hasDeed && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Is there a deed on the plot?</Text>
            </TouchableOpacity>
          </View>

          {hasDeed && (
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Deed Location</Text>
              <TextInput
                style={styles.input}
                value={formData.deedLocation}
                onChangeText={(text) => setFormData({ ...formData, deedLocation: text })}
                placeholder="Where is the deed stored?"
                placeholderTextColor="#A9A9A9"
              />
            </View>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Purchase Price</Text>
            <TextInput
              style={styles.input}
              value={formData.purchasePrice ? `$${formData.purchasePrice}` : ''}
              onChangeText={handleCurrencyChange}
              placeholder="$0.00"
              placeholderTextColor="#A9A9A9"
              keyboardType="decimal-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Purchase Date</Text>
            <TextInput
              style={styles.input}
              value={formData.purchaseDate}
              onChangeText={handleDateChange}
              placeholder="dd/mm/yyyy"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
              maxLength={10}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Cemetery Website</Text>
            <TextInput
              style={styles.input}
              value={formData.cemeteryWebsite}
              onChangeText={(text) => setFormData({ ...formData, cemeteryWebsite: text })}
              placeholder="www.cemetery.com"
              placeholderTextColor="#A9A9A9"
              keyboardType="url"
              autoCapitalize="none"
            />
          </View>

          {/* Portal Toggle */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
            >
              <View style={[styles.toggle, hasOnlinePortal && styles.toggleActive]}>
                <View style={[styles.toggleThumb, hasOnlinePortal && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Does your cemetery have an online portal?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              If yes, enter your login credentials. These will be securely stored in your vault and will only be shared with your successor after your End of Life event.
            </Text>
          </View>

          {hasOnlinePortal && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Cemetery Username 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showUsername}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Cemetery Password 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Additional notes or special instructions"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={styles.addAnotherButton}
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.doneButton}
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {submitting ? (
                <TouchableOpacity style={[styles.submitButton, styles.buttonDisabled]} disabled>
                  <ActivityIndicator color="#FFFFFF" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSubmit}
                >
                  <Text style={styles.submitButtonText}>
                    {isEdit ? 'Update' : 'Submit'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  vaultText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontWeight: '600',
  },
  passwordContainer: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: ms(50),
  },
  eyeButton: {
    position: 'absolute',
    right: ms(15),
    top: ms(15),
    padding: ms(5),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
});

export default BurialPlotInputScreen;